import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/ui/use-toast";
import { Trophy, UserPlus, Mail, User, Briefcase } from "lucide-react";
import { 
  createInvitation, 
  CreateInvitationData, 
  calculateInvitationPoints 
} from "@/services/invitationService";
import { logger } from "@/lib/logger";

// Position options for invited users
const POSITION_OPTIONS = [
  { value: "praktikant", label: "Praktikant", points: 250 },
  { value: "dual-student", label: "Dualer Student", points: 250 },
  { value: "beraterassistent", label: "Beraterassistent", points: 1000 },
  { value: "trainee", label: "Trainee", points: 1000 },
  { value: "berater", label: "Berater", points: 1000 }
];

const formSchema = z.object({
  inviteeName: z.string().min(2, "Name muss mindestens 2 Zeichen haben"),
  inviteeEmail: z.string().email("Ungültige E-Mail-Adresse"),
  inviteePosition: z.string().min(1, "Position ist erforderlich"),
});

type FormData = z.infer<typeof formSchema>;

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvitationCreated: () => void;
}

export function InviteUserDialog({
  open,
  onOpenChange,
  onInvitationCreated,
}: InviteUserDialogProps) {
  const { currentUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      inviteeName: "",
      inviteeEmail: "",
      inviteePosition: "",
    },
  });

  const selectedPosition = form.watch("inviteePosition");
  const selectedPositionData = POSITION_OPTIONS.find(
    (option) => option.value === selectedPosition
  );

  const onSubmit = async (data: FormData) => {
    if (!currentUser?.id) {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Sie müssen angemeldet sein.",
      });
      return;
    }

    if (currentUser.position !== 'praktikant') {
      toast({
        variant: "destructive",
        title: "Berechtigung fehlt",
        description: "Nur Praktikanten können Einladungen senden.",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const invitationData: CreateInvitationData = {
        inviterUserId: currentUser.id,
        inviteeName: data.inviteeName,
        inviteeEmail: data.inviteeEmail,
        inviteePosition: data.inviteePosition,
      };

      const invitationId = await createInvitation(invitationData);
      
      logger.log("Created invitation:", invitationId);
      
      onInvitationCreated();
      form.reset();
      
    } catch (error: any) {
      logger.error("Error creating invitation:", error);
      
      let errorMessage = "Fehler beim Senden der Einladung.";
      if (error.message?.includes("bereits eingeladen")) {
        errorMessage = "Diese E-Mail-Adresse wurde bereits eingeladen.";
      }
      
      toast({
        variant: "destructive",
        title: "Fehler",
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={20} />
            Neue Einladung senden
          </DialogTitle>
          <DialogDescription>
            Laden Sie eine neue Person zu Ihrem Team ein. Nach Admin-Genehmigung erhalten Sie Punkte.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="inviteeName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <User size={16} />
                      Name
                    </FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Vorname Nachname" 
                        {...field}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="inviteeEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Mail size={16} />
                      E-Mail-Adresse
                    </FormLabel>
                    <FormControl>
                      <Input 
                        type="email"
                        placeholder="name@beispiel.de" 
                        {...field}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormDescription>
                      Die Person wird unter dieser E-Mail-Adresse registriert.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="inviteePosition"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Briefcase size={16} />
                      Position
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isSubmitting}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Position auswählen" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {POSITION_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center justify-between w-full">
                              <span>{option.label}</span>
                              <div className="flex items-center gap-1 ml-3 text-xs text-muted-foreground">
                                <Trophy size={12} />
                                {option.points}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Wählen Sie die Position des neuen Teammitglieds.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Points Preview */}
            {selectedPositionData && (
              <Card className="bg-muted/50">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-yellow-500" />
                      <span className="font-medium">Punkte bei Aktivierung:</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-2xl font-bold text-yellow-600">
                        {selectedPositionData.points}
                      </span>
                      <span className="text-sm text-muted-foreground">Punkte</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Sie erhalten diese Punkte, sobald der Administrator die Einladung genehmigt 
                    und den Benutzer aktiviert hat.
                  </p>
                </CardContent>
              </Card>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Abbrechen
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
              >
                {isSubmitting ? "Wird gesendet..." : "Einladung senden"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}